import React, { useState, useEffect } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { useNavigate } from 'react-router-dom';
import { 
  Globe, Users, Video, Languages, Award, ArrowRight, Play,
  CheckCircle, Building2, Handshake, TrendingUp, Sparkles,
  UserPlus, FileText, Share2, BarChart3, MessageSquare
} from 'lucide-react';

// Sample YouTube video IDs - Replace with actual videos
const SAMPLE_VIDEOS = {
  interview: '0kCsjD7mMTw', // Leadership Interview
  testimonial: 'cl47HyJdsbA', // Client Testimonial
  showreel: 'uleWclSnxmk' // Company Showreel
};

// Featured profile to showcase
const FEATURED_PROFILE_SLUG = 'brandstage';

export default function DiscoverPage() {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState(null);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    // Fetch the featured profile data
    const fetchProfile = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/p/${FEATURED_PROFILE_SLUG}`
        );
        if (response.ok) {
          const data = await response.json();
          setProfileData(data);
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      }
    };
    fetchProfile();
  }, []);

  const features = [
    {
      icon: Globe,
      title: 'Instant Global Reach',
      description: '190+ countries, 13 languages — your story travels the world without you lifting a finger'
    },
    {
      icon: Video,
      title: 'Video-First Identity',
      description: 'In a world of text, video builds real trust. Let partners see the people behind the business'
    },
    {
      icon: Award,
      title: 'Built by Trade Experts',
      description: '20 years of Continex cross-border experience, now powering your global discovery'
    },
    {
      icon: Users,
      title: 'Your Ecosystem Starts Here',
      description: 'The first step to communicate and connect with prospective partners who matter'
    }
  ];

  const steps = [
    {
      number: '01',
      icon: UserPlus,
      title: 'Sign Up & Create Profile',
      description: 'Register in minutes. Add your company details, logo, and key information about your business.'
    },
    {
      number: '02',
      icon: Sparkles,
      title: 'AI-Enhanced Content',
      description: 'Our AI helps craft compelling descriptions. Add videos, testimonials, and showcase your achievements.'
    },
    {
      number: '03',
      icon: Languages,
      title: 'Go Multi-Language',
      description: 'Enable auto-translation to reach global audiences in 13 languages including Chinese, Japanese, Arabic, and more.'
    },
    {
      number: '04',
      icon: Video,
      title: 'Add Video Content',
      description: 'Upload leadership interviews, product demos, testimonials, and company showreels.'
    },
    {
      number: '05',
      icon: Share2,
      title: 'Share & Distribute',
      description: 'Get your unique QR code, email signature, and shareable links for trade shows and networking.'
    },
    {
      number: '06',
      icon: BarChart3,
      title: 'Track & Engage',
      description: 'Monitor profile views, collect testimonials, and receive connection requests from interested parties.'
    }
  ];

  const VideoModal = ({ videoId, title, onClose }) => (
    <div 
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-4xl aspect-video relative"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-zinc-300 text-sm"
        >
          Close
        </button>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title={title}
          className="w-full h-full rounded-xl"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Video Modal */}
      {activeVideo && (
        <VideoModal 
          videoId={activeVideo.id} 
          title={activeVideo.title}
          onClose={() => setActiveVideo(null)}
        />
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#C41E3A]/10 via-zinc-950 to-[#1E3A5F]/10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#C41E3A]/5 rounded-full blur-3xl" />
        
        <div className="relative max-w-5xl mx-auto px-4 py-20 md:py-28">
          {/* Welcome Badge */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-3 bg-zinc-900/60 border border-zinc-800 rounded-full px-5 py-2.5 backdrop-blur-sm">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-zinc-300 text-sm">Welcome to BrandStage</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-white mb-8 leading-tight">
            Your Identity.<br />
            <span className="text-[#C41E3A]">Your Global Stage.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-zinc-300 text-center max-w-3xl mx-auto mb-6 leading-relaxed">
            Welcome to a space where businesses like yours step into the global spotlight — 
            elegantly, effortlessly, and authentically.
          </p>

          <p className="text-lg text-zinc-400 text-center max-w-2xl mx-auto mb-10 leading-relaxed">
            In today's connected world, opportunities don't wait. BrandStage helps you build 
            a presence that speaks for you — reaching the right partners, in the right markets, 
            in their language, before your first conversation begins.
          </p>
          
          {/* Aspirational tagline */}
          <div className="text-center mb-12">
            <p className="text-zinc-500 text-sm uppercase tracking-widest mb-2">Think of it as</p>
            <p className="text-xl md:text-2xl text-white font-light italic">
              "Your first-class lounge in the digital world"
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/register')}
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center gap-2 shadow-lg shadow-[#C41E3A]/25"
            >
              Create Your Profile
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="bg-zinc-800 hover:bg-zinc-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all border border-zinc-700"
            >
              See How It Works
            </button>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-16 text-zinc-500 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>No Technical Skills Needed</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>Live in Minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>AI-Assisted Content</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why BrandStage Section */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Makes BrandStage Different
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              In the complex digital world, this is your first step to building an ecosystem 
              that helps you communicate and connect with the partners who matter.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-[#C41E3A]/50 transition-all group"
              >
                <div className="w-12 h-12 bg-[#C41E3A]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#C41E3A]/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-[#C41E3A]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-zinc-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Profile Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              See a Live Profile
            </h2>
            <p className="text-zinc-400 text-lg">
              Here's what a complete BrandStage profile looks like
            </p>
          </div>

          <div className="relative">
            {/* Profile Preview Card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden max-w-4xl mx-auto">
              {/* Profile Header */}
              <div className="bg-gradient-to-r from-[#C41E3A]/20 to-[#1E3A5F]/20 p-8">
                <div className="flex items-start gap-6">
                  {profileData?.logo_url ? (
                    <img 
                      src={profileData.logo_url} 
                      alt={profileData.company_name}
                      className="w-20 h-20 object-contain bg-white rounded-xl p-2"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-[#C41E3A] rounded-xl flex items-center justify-center">
                      <Building2 className="w-10 h-10 text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {profileData?.company_name || 'BrandStage'}
                    </h3>
                    <p className="text-zinc-300 mt-1">
                      {profileData?.tagline || 'Your Global Business Profile Platform'}
                    </p>
                    <div className="flex items-center gap-4 mt-3 text-sm text-zinc-400">
                      {profileData?.headquarters && (
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {profileData.headquarters}
                        </span>
                      )}
                      {profileData?.year_established && (
                        <span>Since {profileData.year_established}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Content Preview */}
              <div className="p-8">
                <p className="text-zinc-300 leading-relaxed mb-6">
                  {profileData?.profile_text?.substring(0, 300) || 
                    'This profile showcases how businesses present themselves on BrandStage — with compelling narratives, video content, multi-language support, and professional branding that travels globally.'}
                  {profileData?.profile_text?.length > 300 && '...'}
                </p>

                {/* Feature badges */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="bg-[#1E3A5F]/30 text-[#6B9FD4] text-xs px-3 py-1 rounded-full">Video Enabled</span>
                  <span className="bg-emerald-900/30 text-emerald-400 text-xs px-3 py-1 rounded-full">Multi-Language</span>
                  <span className="bg-amber-900/30 text-amber-400 text-xs px-3 py-1 rounded-full">Trade Show Ready</span>
                </div>

                <button
                  onClick={() => window.open(`/p/${FEATURED_PROFILE_SLUG}`, '_blank')}
                  className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  View Full Profile
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#C41E3A]/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#1E3A5F]/10 rounded-full blur-2xl" />
          </div>
        </div>
      </section>

      {/* Video Samples Section */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              See BrandStage in Action
            </h2>
            <p className="text-zinc-400 text-lg">
              Real examples of how businesses use video content to connect globally
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Interview Video */}
            <div className="group">
              <div 
                className="relative aspect-video bg-zinc-800 rounded-xl overflow-hidden cursor-pointer mb-4"
                onClick={() => setActiveVideo({ id: SAMPLE_VIDEOS.interview, title: 'Leadership Interview' })}
              >
                <img 
                  src={`https://img.youtube.com/vi/${SAMPLE_VIDEOS.interview}/maxresdefault.jpg`}
                  alt="Interview thumbnail"
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=400&fit=crop'}
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-[#C41E3A] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-lg mb-1">Leadership Interview</h3>
              <p className="text-zinc-400 text-sm">See how executives share their vision and company story</p>
            </div>

            {/* Testimonial Video */}
            <div className="group">
              <div 
                className="relative aspect-video bg-zinc-800 rounded-xl overflow-hidden cursor-pointer mb-4"
                onClick={() => setActiveVideo({ id: SAMPLE_VIDEOS.testimonial, title: 'Client Testimonial' })}
              >
                <img 
                  src={`https://img.youtube.com/vi/${SAMPLE_VIDEOS.testimonial}/maxresdefault.jpg`}
                  alt="Testimonial thumbnail"
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop'}
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-[#C41E3A] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-lg mb-1">Client Testimonial</h3>
              <p className="text-zinc-400 text-sm">Authentic testimonials build trust with potential partners</p>
            </div>

            {/* Showreel Video */}
            <div className="group">
              <div 
                className="relative aspect-video bg-zinc-800 rounded-xl overflow-hidden cursor-pointer mb-4"
                onClick={() => setActiveVideo({ id: SAMPLE_VIDEOS.showreel, title: 'Company Showreel' })}
              >
                <img 
                  src={`https://img.youtube.com/vi/${SAMPLE_VIDEOS.showreel}/maxresdefault.jpg`}
                  alt="Showreel thumbnail"
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&h=400&fit=crop'}
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-[#C41E3A] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
              </div>
              <h3 className="text-white font-semibold text-lg mb-1">Company Showreel</h3>
              <p className="text-zinc-400 text-sm">Professional showreels showcase capabilities at a glance</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How to Get Started
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              From sign-up to global visibility in just a few steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="relative bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-all"
              >
                {/* Step number */}
                <div className="absolute -top-3 -left-3 w-10 h-10 bg-[#C41E3A] rounded-xl flex items-center justify-center text-white font-bold text-sm">
                  {step.number}
                </div>
                
                <div className="pt-4">
                  <div className="w-12 h-12 bg-zinc-800 rounded-xl flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-[#C41E3A]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-zinc-400 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Everything You Get
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="space-y-4">
              {[
                'Professional company profile page',
                'AI-assisted content generation',
                'Multi-language auto-translation (13 languages)',
                'Video embedding (YouTube, interviews, showreels)',
                'Leadership & team profiles',
                'Client testimonial collection system'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-300">{item}</span>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              {[
                'QR codes for trade shows & events',
                'Email signature strip generator',
                'Analytics & visitor tracking',
                'Gated downloads (brochures, catalogs)',
                'BrandStage Showcase for events',
                'Profile sharing & social integration'
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span className="text-zinc-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-[#C41E3A]/20 to-[#1E3A5F]/20 border border-zinc-800 rounded-3xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-[#C41E3A] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Globe className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Go Global?
            </h2>
            
            <p className="text-zinc-300 text-lg mb-8 max-w-2xl mx-auto">
              Take the first step towards building your global presence. 
              Connect with partners, distributors, and clients worldwide.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <button
                onClick={() => navigate('/register')}
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center gap-2 shadow-lg shadow-[#C41E3A]/25"
              >
                Create Your Free Account
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <p className="text-zinc-500 text-sm">
              Free to start • No credit card required • Go live in minutes
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <BrandLogo className="h-10" />
            </div>
            <p className="text-zinc-500 text-sm text-center md:text-left">
              Powered by Continex • 20 Years of Cross-Border Business Expertise
            </p>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="text-zinc-400 hover:text-white text-sm transition-colors"
              >
                Home
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="text-zinc-400 hover:text-white text-sm transition-colors"
              >
                Login
              </button>
              <button 
                onClick={() => navigate('/register')}
                className="text-zinc-400 hover:text-white text-sm transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
